import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useGReverseGeocodeQuery } from "../../../../api/network/mapApiServices";
import { setModalsData, TModalsState } from "../../../../api/store/commonSlice";
import { CameraWakeupRequiredInfo } from "../../../../api/types/Camera";
import {
  AIComponent,
  CameraDetail,
  CameraHealth,
  DeviceReportSimple,
  DeviceReportSummary,
  FootageStorage,
  GPSDetail,
} from "../../../../api/types/CamerasAdminVM";
import CAM_K145C from "../../../../assets/images/cam_K145C_K245C.png";
import CAM_K165 from "../../../../assets/images/cam_K165_K265.png";
import CAM_K220 from "../../../../assets/images/cam_K220.png";
import Check from "../../../../assets/svg/check-ring.svg";
import SignalIcon from "../../../../assets/svg/network.svg";
import SatelliteIcon from "../../../../assets/svg/satellite.svg";
import SignalUp from "../../../../assets/svg/signal_up.svg";
import Unhealthy from "../../../../assets/svg/unhealthy.svg";
import {
  AdminFormFieldInput,
  AdminFormFieldSubmit,
} from "../../../../components/admin/formFields";
import Tooltip from "../../../../components/cameraAdmin/tooltip";
import { APP_CONFIG, CAM_TYPE } from "../../../../constants/constants";
import { useAccountTimezone } from "../../../../hooks/timezone";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../../../../utils/dateTime";
import {
  CONVERT_CAMERA_MODEL_NAMES,
  SIGNAL_STATUS_OPTIONS,
} from "../../constants";
import ModalDiagnoseCamera from "../modalDiagnoseCamera";

interface ScreenCameraOverviewProps {
  cameraData: CameraDetail | null;
  reportData: DeviceReportSummary | null;
  simpleHealthData: DeviceReportSimple | null;
}

const ScreenCameraOverview = (props: ScreenCameraOverviewProps) => {
  const { cameraIdAssetId } = useParams<{ cameraIdAssetId: string }>();
  const [cameraId, assetId] = (cameraIdAssetId || "")?.split("__");

  const dispatch = useDispatch();
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const { tzForDateFns } = useAccountTimezone();
  const { cameraData: dataDeviceDetails, simpleHealthData } = props;
  const simpleReportData = simpleHealthData?.report;
  const comprehensiveReportData = props.reportData?.report;

  const { t } = useTranslation("translation", {
    keyPrefix:
      "camera.dashboard.management.cameras.camera_details.tabs.overview",
  });

  const satellitesInUse =
    comprehensiveReportData?.health_gps?.filter(
      (detail: GPSDetail) => detail.type === "in_use_satellite_count"
    )?.[0]?.status ?? 0;

  const uploadSpeed =
    comprehensiveReportData?.health_network?.upload_speed?.status;
  const uploadSpeedinKbps =
    uploadSpeed && typeof uploadSpeed === "number"
      ? (uploadSpeed / 1000).toFixed(2)
      : 0;

  const { data: dataAddress } = useGReverseGeocodeQuery(
    {
      latitude: dataDeviceDetails?.latest_lat ?? 0,
      longitude: dataDeviceDetails?.latest_lng ?? 0,
    },
    {
      skip: !dataDeviceDetails,
    }
  );

  const geocodeAddress = dataAddress?.results[0]?.formatted_address ?? "";

  /**
   * Method to return camera image by its model type
   * @param modelName
   * @returns Image of camera
   */
  const getCameraImageByModel = (modelName: string) => {
    const upperModel = modelName.toUpperCase();
    switch (upperModel) {
      case CAM_TYPE.K220:
        return CAM_K220;
      case CAM_TYPE.K145C:
      case CAM_TYPE.K245C:
        return CAM_K145C;
      case CAM_TYPE.K165:
      case CAM_TYPE.K265:
        return CAM_K165;
      default:
        return CAM_K220;
    }
  };

  const OverviewInfo = [
    {
      title: t("serial_num"),
      value: dataDeviceDetails?.serial_number,
    },
    {
      title: t("model"),
      value: CONVERT_CAMERA_MODEL_NAMES
        ? dataDeviceDetails?.sku_name.replace("K", "FV")
        : dataDeviceDetails?.sku_name,
    },
    {
      title: t("imei"),
      value: dataDeviceDetails?.IMEI_1,
    },
  ];

  const sanitizeCameraIssueDescrtion = (description: string) => {
    return description
      .replace("[embedded_inward]", "Driver Facing Camera")
      .replace("[embedded_outward]", "Road Facing Camera");
  };

  /**
   * Method to determine overall health status of cameras
   * @param data Camera health data for all cameras
   * @returns Overall health status
   */
  const getCameraHealthByReport = (data: CameraHealth | null) => {
    if (!data) {
      return {
        status: "",
        description: "",
      };
    }
    const { embedded_inward, embedded_outward, tvi, uvc } = data;
    const status =
      embedded_inward.status === "unhealthy" ||
      embedded_outward.status === "unhealthy" ||
      tvi.status === "unhealthy" ||
      uvc.status === "unhealthy"
        ? "unhealthy"
        : "healthy";

    let description = "";
    if (status === "unhealthy") {
      if (embedded_inward.status === "unhealthy") {
        description = `${embedded_inward.description} ${embedded_inward.solution}`;
      } else if (embedded_outward.status === "unhealthy") {
        description = `${embedded_outward.description} ${embedded_outward.solution}`;
      } else if (tvi.status === "unhealthy") {
        description = `${tvi.description} ${tvi.solution}`;
      } else if (uvc.status === "unhealthy") {
        description = `${uvc.description} ${uvc.solution}`;
      }
    }
    /**
     * Sanitize the description to replace camera names with more user-friendly terms
     */
    description = sanitizeCameraIssueDescrtion(description);

    return {
      status,
      description,
    };
  };

  const getAIHealthByReport = (data: AIComponent | null) => {
    if (!data) {
      return {
        status: "",
        description: "",
      };
    }
    const { embedded_inward, embedded_outward, uvc } = data;
    const status =
      embedded_inward.status === "unhealthy" ||
      embedded_outward.status === "unhealthy" ||
      uvc.status === "unhealthy"
        ? "unhealthy"
        : "healthy";
    let description = "";
    if (status === "unhealthy") {
      if (embedded_inward.status === "unhealthy") {
        description = `${embedded_inward.description} ${embedded_inward.solution}`;
      } else if (embedded_outward.status === "unhealthy") {
        description = `${embedded_outward.description} ${embedded_outward.solution}`;
      } else if (uvc.status === "unhealthy") {
        description = `${uvc.description} ${uvc.solution}`;
      }
    }

    /**
     * Sanitize the description to replace camera names with more user-friendly terms
     */
    description = sanitizeCameraIssueDescrtion(description);

    return {
      status,
      description,
    };
  };

  /**
   * Method to determine overall footage health
   * @param data Footage Storage data for SD cards
   * @returns status and description of overall footage cards
   */
  const getFootageHealthFromReport = (data: FootageStorage | null) => {
    if (!data) {
      return {
        status: "",
        description: "",
      };
    }
    const { sd_card_1, sd_card_2 } = data;
    const status =
      sd_card_1.status === "unhealthy" || sd_card_2.status === "unhealthy"
        ? "unhealthy"
        : "healthy";
    let description = "";
    if (status === "unhealthy") {
      if (sd_card_1.status === "unhealthy") {
        description = `${sd_card_1.description} ${sd_card_1.solution}`;
      } else {
        description = `${sd_card_2.description} ${sd_card_2.solution}`;
      }
    }

    return {
      status,
      description,
    };
  };

  const healthData = [
    {
      title: t("location_fun"),
      status: simpleReportData?.location_function?.status,
      description: `${simpleReportData?.location_function?.description} ${simpleReportData?.location_function.solution}`,
    },
    {
      title: t("sensor_event_detection"),
      status: simpleReportData?.sensor_events_detection?.status,
      description: `${simpleReportData?.sensor_events_detection?.description} ${simpleReportData?.sensor_events_detection.solution}`,
    },
    {
      title: t("miai"),
      ...getAIHealthByReport(simpleReportData?.ai_component ?? null),
    },
    {
      title: t("cameras"),
      ...getCameraHealthByReport(simpleReportData?.camera ?? null),
    },
    {
      title: t("storage_issue"),
      ...getFootageHealthFromReport(simpleReportData?.footage_storage ?? null),
    },
  ];

  const onDiagnoseClick = () => {
    // Id camera is oline, then show doagnostic modal else show wakeup modal
    if (dataDeviceDetails?.is_online) {
      dispatch(
        setModalsData({ ...modalsState, showModalDiagnoseCamera: true })
      );
    } else {
      if (!!dataDeviceDetails?.cid && dataDeviceDetails?.serial_number) {
        dispatch(
          setModalsData({
            ...modalsState,
            showModalWakeupCamera: {
              cid: dataDeviceDetails?.cid,
              serial_number: dataDeviceDetails?.serial_number,
              onSuccessCallback: onCameraWakeupSuccess,
            } as CameraWakeupRequiredInfo,
          })
        );
      } else {
        if (APP_CONFIG.DEBUG.ALL)
          console.log("cid or serial number is not available");
      }
    }
  };

  const onCameraWakeupSuccess = (serailNum: string) => {
    dispatch(
      setModalsData({
        ...modalsState,
        showModalWakeupCamera: null,
        showModalDiagnoseCamera: true,
      })
    );
  };

  return (
    <div className="col-span-9 space-y-4">
      <h1 className="font-semibold text-base leading-none align-middle col-span-9">
        {t("overview")}
      </h1>
      <div className="p-4 bg-gray-100 col-span-9 flex rounded-lg grid grid-cols-12 gap-4">
        <div className="bg-white border rounded-lg border-gray-200 place-items-center col-span-4">
          <img
            src={getCameraImageByModel(dataDeviceDetails?.sku_name ?? "")}
            width={200}
            height={180}
            alt="Camera"
          />
        </div>
        {/* <pre className="col-span-12">
          {JSON.stringify(dataDeviceDetails, null, 2)}
        </pre> */}
        <div className="col-span-8">
          <AdminFormFieldInput
            // label={"Device Name"}
            label={"Asset ID"}
            type="text"
            id="vehicle_make"
            name="vehicle_make"
            // value={dataDeviceDetails?.device_name}
            value={!!assetId && assetId !== "null" ? assetId : "-"}
            onChange={() => {}}
            disabled={true}
            detailsFormField={true}
          />
          <div className="grid grid-cols-9 col-span-8 gap-4 mt-4">
            {OverviewInfo.map((infoItem, index) => {
              return (
                <div className="col-span-3" key={index}>
                  <h2 className="font-semibold text-sm leading-5 tracking-tight align-middle">
                    {infoItem.title}
                  </h2>
                  <span className="text-sm font-light align-middle">
                    {infoItem.value}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Signal Details */}
      <h1 className="font-semibold text-base leading-none align-middle col-span-9">
        {t("signal")}
      </h1>
      <div className="bg-gray-100 col-span-9 grid grid-cols-9 rounded-lg p-4 min-h-64">
        <h2 className="font-semibold text-sm leading-5 tracking-tight align-middle col-span-9">
          {t("satellite_in_use")}
        </h2>
        <div className="flex flex-row col-span-9">
          <h2 className="font-medium text-sm">{satellitesInUse}</h2>
          <img src={SignalIcon} alt="network" className="w-5 h-5 ml-2" />
          {comprehensiveReportData?.latest_gps_signal ===
            SIGNAL_STATUS_OPTIONS.no_signal && (
            <Tooltip description={t("gps_warning")} icon={Unhealthy} />
          )}
          <img
            src={SatelliteIcon}
            alt="satellite"
            className="w-5 h-5 ml-1 ml-2"
          />
        </div>
        <h2 className="font-semibold text-sm leading-5 tracking-tight align-middle col-span-9">
          {t("last_location")}
        </h2>
        <span className="col-span-9 font-medium text-sm">{geocodeAddress}</span>
        <h2 className="font-semibold text-sm leading-5 tracking-tight align-middle col-span-9">
          {t("lte_signal")}
        </h2>
        <div className="flex flex-row col-span-9">
          <img src={SignalIcon} alt="network" className="w-5 h-5 mr-2" />
          <img src={SignalUp} alt="signal" className="w-5 h-5 mr-2" />
          <span className="font-medium text-sm col-span-9">{`${uploadSpeedinKbps} Kb/s`}</span>
        </div>
      </div>

      {/* Device Health */}
      <div className="flex flex-row items-center justify-between">
        <h2 className="font-semibold text-base leading-none align-middle">
          {t("device_health_check")}
        </h2>
        <AdminFormFieldSubmit
          buttonDimensionClass={"h-10 w-32 px-6"}
          type="button"
          variant="primary"
          label={
            <span className="flex justify-center whitespace-nowrap">
              {t("diagnose_now")}
            </span>
          }
          onClick={onDiagnoseClick}
        />
      </div>
      <div className="bg-gray-50 rounded-lg px-4 py-4 grid grid-cols-12 gap-2 min-h-64 col-span-9">
        {healthData?.map((healthItem, i) => {
          return (
            <div className="col-span-6" key={i}>
              <h2 className="font-semibold text-sm leading-5 tracking-tight align-middle">
                {healthItem.title}
              </h2>
              <div className="flex flex-row items-center my-2">
                <img
                  src={healthItem?.status === "healthy" ? Check : Unhealthy}
                  className="h-5 w-5 mr-2"
                  alt="health"
                />
                <span className="font-medium text-sm">
                  {healthItem?.status === "healthy" ? "Healthy" : "Unhealthy"}
                </span>
                {healthItem?.status === "unhealthy" && (
                  <Tooltip description={healthItem.description} />
                )}
              </div>
            </div>
          );
        })}
        <h2 className="font-md text-sm text-gray-400 leading-5 tracking-tight align-middle col-span-12 mt-4">
          {t("last_diagnose")}
        </h2>
        <span className="text-xs text-gray-400 col-span-12">
          {formattedDateTimeBasedOnTimezone(
            dbTimeToJsTime(simpleHealthData?.report?.version ?? 0),
            tzForDateFns,
            APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
          ) || "-"}
        </span>
      </div>
      <ModalDiagnoseCamera cameraId={dataDeviceDetails?.cid ?? ""} />
      {/* Wakeup camera modal is moved to `src/components/common/VMCameraVoicecalling.tsx` */}
    </div>
  );
};

export default ScreenCameraOverview;
